import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import {
  PATH_MATERIAL_COSTS,
  PATH_MATERIAL_COSTS_EDIT,
} from '../../../app/constants/paths.constant';
import { RightDrawer } from '../../../components/RightDrawer';
import { SpeedDial } from '../../../components/SpeedDial';
import { FormVariant } from '../../../forms';
import { MaterialForm, MaterialFormTab } from '../../../forms/Material';
import { useBuildPaths } from '../../../hooks/useBuildPaths.hook';

import { List } from './List';

export const MaterialPage_Costs_Material_Fragment = graphql(`
  fragment MaterialPage_Costs_Material_Fragment on Material {
    _id
    costUsages {
      ...MaterialPage_CostUsagesList_CostUsage_Fragment
    }
    ...MaterialForm_Material_Fragment
  }
`);

interface Props {
  readonly material: FragmentType<typeof MaterialPage_Costs_Material_Fragment>;
}

export const Costs = ({ material: pMaterial }: Readonly<Props>) => {
  const material = getFragmentData(
    MaterialPage_Costs_Material_Fragment,
    pMaterial,
  );

  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();

  const { paths } = useBuildPaths(
    {
      consult: PATH_MATERIAL_COSTS,
      edit: PATH_MATERIAL_COSTS_EDIT,
    },
    { id: material._id },
  );

  const isInEditionMode = currentPath === paths.edit;
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  return (
    <>
      <List costUsages={material.costUsages} />
      <SpeedDial
        actions={[
          {
            id: 'editMaterialCosts',
            icon: <EditIcon />,
            name: 'Modifier Coûts Liés',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Coûts Liés au Matériel"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <MaterialForm
          formVariant={FormVariant.Modification}
          initialValues={material}
          materialId={material._id}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          stepIdsSubset={[
            MaterialFormTab.CostsSelection,
            MaterialFormTab.CostsOrder,
            MaterialFormTab.CostsUsage,
          ]}
          title="Formulaire de modification des Coûts Liés au Matériel"
        />
      </RightDrawer>
    </>
  );
};
